/**
 * Phase 5B: cumulative equity curve from `aggregate.equity_curve` —
 * `{date, pnl, equity, trades}` row shape. Recharts AreaChart.
 *
 * The synthetic today-live row (when present) is marked with a different
 * dot colour so operators can see the live overlay's contribution at a
 * glance.
 */
import React, { useMemo } from 'react';
import {
  Area, AreaChart, CartesianGrid, ResponsiveContainer,
  Tooltip as RechartsTooltip, XAxis, YAxis,
} from 'recharts';
import type { MarketplaceEquityPoint } from '../../types/marketplaceAnalytics';

interface Props {
  equityCurve: MarketplaceEquityPoint[];
  height?: number;
}

const fmtInr = (n: number) =>
  n.toLocaleString('en-IN', { maximumFractionDigits: 0 });

const MarketplaceEquityChart: React.FC<Props> = ({ equityCurve, height = 280 }) => {
  const data = useMemo(() => (
    (equityCurve || []).map((p) => ({
      date: p.date,
      equity: p.equity,
      pnl: p.pnl,
      trades: p.trades,
    }))
  ), [equityCurve]);

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-sm text-gray-500 dark:text-gray-400">
        No equity data yet for this window.
      </div>
    );
  }

  return (
    <div style={{ height }} className="w-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: 8 }}>
          <defs>
            <linearGradient id="mp-equity-grad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.45} />
              <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.25)" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11, fill: 'currentColor' }}
            tickLine={false}
            axisLine={{ stroke: 'rgba(148,163,184,0.4)' }}
            minTickGap={32}
          />
          <YAxis
            tickFormatter={(v: number) => fmtInr(v)}
            tick={{ fontSize: 11, fill: 'currentColor' }}
            tickLine={false}
            axisLine={{ stroke: 'rgba(148,163,184,0.4)' }}
            width={80}
          />
          <RechartsTooltip
            contentStyle={{
              fontSize: 12,
              borderRadius: 6,
              border: '1px solid rgba(148,163,184,0.4)',
              background: 'rgba(255,255,255,0.95)',
            }}
            labelStyle={{ fontWeight: 600, color: '#111827' }}
            formatter={(value, name) => {
              const v = Number(value ?? 0);
              if (name === 'equity') return [fmtInr(v), 'Equity'];
              return [fmtInr(v), String(name)];
            }}
          />
          <Area
            type="monotone"
            dataKey="equity"
            stroke="#3b82f6"
            strokeWidth={2}
            fill="url(#mp-equity-grad)"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default MarketplaceEquityChart;
